/**
 * Toast Store
 *
 * Manages toast notifications with auto-dismiss functionality.
 * Toasts are displayed at the top-right of the screen and auto-dismiss
 * after a configurable duration.
 *
 * Also maintains a history of all toasts for later review.
 * History is persisted to the backend (Redis) for cross-session persistence.
 */

import { create } from 'zustand';

// Setter pattern to avoid circular dependency (WebSocketService imports from toastStore)
let wsServiceInstance: any = null;
export const setWsServiceForToast = (ws: any) => {
  wsServiceInstance = ws;
};
const getWsService = () => wsServiceInstance;

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // ms, defaults to 3000
  /** Optional button on the toast (e.g. Undo). Clicking it runs onClick and dismisses the toast. */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastHistoryItem extends Omit<Toast, 'action'> {
  timestamp: number;
}

interface ToastState {
  toasts: Toast[];
  history: ToastHistoryItem[];
  historyVisible: boolean;
  historyLoaded: boolean;
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  clearHistory: () => void;
  setHistoryVisible: (visible: boolean) => void;
  toggleHistoryVisible: () => void;
  setHistory: (history: ToastHistoryItem[]) => void;
  fetchHistory: () => void;
}

const DEFAULT_DURATION = 3000; // 3 seconds
const MAX_HISTORY_SIZE = 100; // Keep last 100 toasts

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  history: [],
  historyVisible: false,
  historyLoaded: false,

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? DEFAULT_DURATION,
    };

    // Create history item (without the action: a callback is neither
    // replayable from history nor serialisable to the backend)
    const { action: _action, ...historyFields } = newToast;
    const historyItem: ToastHistoryItem = {
      ...historyFields,
      timestamp,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
      // Add to history, keeping max size
      history: [...state.history, historyItem].slice(-MAX_HISTORY_SIZE),
    }));

    // Send to backend for persistence (use lazy getter to avoid circular dep)
    try {
      getWsService()?.emit('toast:add', historyItem);
    } catch (e) {
      // Ignore if wsService not ready
    }

    // Auto-dismiss after duration (0 = persistent, requires manual close)
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, newToast.duration);
    }

    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [] });
  },

  clearHistory: () => {
    set({ history: [] });
    // Clear on backend too
    try {
      getWsService()?.emit('toast:clear-history', {});
    } catch (e) {
      // Ignore if wsService not ready
    }
  },

  setHistoryVisible: (visible) => {
    set({ historyVisible: visible });
  },

  toggleHistoryVisible: () => {
    set((state) => ({ historyVisible: !state.historyVisible }));
  },

  setHistory: (history) => {
    set({ history, historyLoaded: true });
  },

  fetchHistory: () => {
    if (get().historyLoaded) return;
    try {
      getWsService()?.emit('toast:get-history', {});
    } catch (e) {
      // Ignore if wsService not ready
    }
  },
}));

// Initialize socket listener for history response
let historyListenerInitialized = false;

export function initToastHistoryListener(): void {
  if (historyListenerInitialized) return;
  historyListenerInitialized = true;

  const ws = getWsService();
  if (!ws) return;

  ws.on('toast:history', (data: { success: boolean; history: ToastHistoryItem[] }) => {
    if (data.success && data.history) {
      useToastStore.getState().setHistory(data.history);
      console.log(`[TOAST] Loaded ${data.history.length} history items from backend`);
    }
  });

  ws.on('toast:history-cleared', (data: { success: boolean }) => {
    if (data.success) {
      console.log('[TOAST] History cleared on backend');
    }
  });
}

export function resetToastSocketHandlers() {
  historyListenerInitialized = false;
  console.log('[TOAST] Reset socket handlers flag');
}

// Convenience function for adding toasts without importing the store
export const addToast = (
  type: ToastType,
  message: string,
  duration?: number
) => {
  return useToastStore.getState().addToast({ type, message, duration: duration ?? DEFAULT_DURATION });
};

// Convenience function for toggling history visibility
export const toggleToastHistory = () => {
  useToastStore.getState().toggleHistoryVisible();
};
